import { api } from './api.ts';
import { decrypt, encrypt, fromBase64, toBase64, utf8 } from './crypto/primitives.ts';
import {
  generateKeyPair,
  importPrivateKey,
  unwrapBytes,
  wrapBytesFor,
  wrapDekFor,
  type KeyAlgorithm,
} from './crypto/keypair.ts';
import { deriveKreisBezeichnungsSchluessel } from './crypto/kdf.ts';
import { requireOrganisation, requireVault } from './crypto/vault.ts';

/**
 * Schlüsselkreise (Migration 007/010): Ordner und Marker als Einheit des
 * Teilens innerhalb einer Organisation. Ausführliche Begründung in
 * `backend/src/routes/schluesselkreise.ts` — hier nur die Client-Seite: die
 * Kette der Umschläge auflösen und neue Kreise anlegen.
 */

export interface Kreis {
  id: string;
  /** Safe, zu dem der Kreis gehört (seit 17.10.2026 über alle Safes geladen). */
  kundeId?: string;
  /** Fach: der Ordner, an dem der Kreis hängt (Migration 020). */
  folderId?: string | null;
  /** Personen mit eigenem Umschlag (nur Kennungen). */
  personen?: string[];
  art: 'ordner' | 'marker';
  elternId: string | null;
  algorithm: KeyAlgorithm;
  publicKey: string;
  bezeichnungEnc: string;
  erstelltAm: string;
  wrappedForParent: string | null;
  wrappedPrivateKeyForSelf: string | null;
}

export interface GeoeffneterKreis {
  id: string;
  kundeId: string | null;
  folderId: string | null;
  personen: string[];
  art: 'ordner' | 'marker';
  elternId: string | null;
  bezeichnung: string;
  algorithm: KeyAlgorithm;
  publicKeyRaw: Uint8Array;
  privateKey: CryptoKey;
  /** Für's Weiterwrappen (Dokument hineinlegen, weiteres Mitglied aufnehmen). */
  privateKeyPkcs8: Uint8Array;
}

/**
 * Lädt alle Kreise der aktuell gewählten Organisation und schließt so viele
 * wie möglich auf: erst die direkt für die eigene Person gewrappten, dann
 * iterativ jeden Unterordner, dessen Elternordner gerade aufgeschlossen
 * wurde. Ein Kreis ohne erreichbaren Umschlag bleibt einfach draußen — kein
 * Fehler, sondern der Normalfall für einen Kreis, dem man nicht angehört.
 */
/**
 * Zwischenspeicher der zuletzt geladenen, entschlüsselten Kreise —
 * `openDocument` in `documents.ts` braucht ihn, um ein für einen Kreis
 * gewrapptes Dokument zu öffnen, ohne bei jedem einzelnen Dokument erneut
 * `GET /api/kreise` samt Umschlagkette aufzulösen.
 */
let kreisCache: GeoeffneterKreis[] | null = null;

/** Leert den Zwischenspeicher — beim Abmelden und beim Organisationswechsel. */
export function kreisCacheLeeren(): void {
  kreisCache = null;
}

export async function ladeKreiseGecacht(): Promise<GeoeffneterKreis[]> {
  kreisCache ??= await ladeUndEntschluessleKreise();
  return kreisCache;
}

/** Nach Anlegen/Löschen eines Kreises: verwirft den Zwischenspeicher und lädt neu. */
export async function kreiseAktualisieren(): Promise<GeoeffneterKreis[]> {
  kreisCache = null;
  return ladeKreiseGecacht();
}

/** Liefert den entsperrten privaten Schlüssel eines Kreises, falls erreichbar. */
export async function kreisSchluessel(
  kreisId: string,
): Promise<{ algorithm: KeyAlgorithm; privateKey: CryptoKey } | null> {
  const kreise = await ladeKreiseGecacht();
  const treffer = kreise.find((k) => k.id === kreisId);
  return treffer ? { algorithm: treffer.algorithm, privateKey: treffer.privateKey } : null;
}

export async function ladeUndEntschluessleKreise(): Promise<GeoeffneterKreis[]> {
  // Über alle Safes, in denen man Mitglied ist – nicht nur die gewählte Organisation.
  const { kreise } = await api.get<{ kreise: Kreis[] }>('/api/safes/kreise');
  const vault = requireVault();

  // Rohe PKCS8-Bytes bleiben hier zusätzlich zum importierten CryptoKey
  // vorrätig — die Bezeichnung eines Kreises leitet ihren Schlüssel direkt
  // aus diesen Bytes ab (kdf.ts, deriveKreisBezeichnungsSchluessel), nicht
  // aus dem nicht-exportierbaren CryptoKey.
  const roheSchluessel = new Map<string, Uint8Array>();
  const geoeffnet = new Map<string, GeoeffneterKreis>();
  const uebrig = new Map(kreise.map((k) => [k.id, k]));

  for (const k of kreise) {
    if (!k.wrappedPrivateKeyForSelf) continue;
    const bytes = await unwrapBytes(
      fromBase64(k.wrappedPrivateKeyForSelf),
      vault.privateKey,
      vault.algorithm,
    );
    roheSchluessel.set(k.id, bytes);
    geoeffnet.set(k.id, {
      id: k.id,
      kundeId: k.kundeId ?? null,
      folderId: k.folderId ?? null,
      personen: k.personen ?? [],
      art: k.art,
      elternId: k.elternId,
      bezeichnung: '',
      algorithm: k.algorithm,
      publicKeyRaw: fromBase64(k.publicKey),
      privateKey: await importPrivateKey(bytes, k.algorithm),
      privateKeyPkcs8: bytes,
    });
    uebrig.delete(k.id);
  }

  let geaendert = true;
  while (geaendert) {
    geaendert = false;
    for (const k of uebrig.values()) {
      if (!k.elternId || !k.wrappedForParent) continue;
      const eltern = geoeffnet.get(k.elternId);
      if (!eltern) continue;
      const bytes = await unwrapBytes(fromBase64(k.wrappedForParent), eltern.privateKey, eltern.algorithm);
      roheSchluessel.set(k.id, bytes);
      geoeffnet.set(k.id, {
        id: k.id,
        kundeId: k.kundeId ?? null,
        folderId: k.folderId ?? null,
        personen: k.personen ?? [],
        art: k.art,
        elternId: k.elternId,
        bezeichnung: '',
        algorithm: k.algorithm,
        publicKeyRaw: fromBase64(k.publicKey),
        privateKey: await importPrivateKey(bytes, k.algorithm),
        privateKeyPkcs8: bytes,
      });
      uebrig.delete(k.id);
      geaendert = true;
    }
  }

  const ergebnis: GeoeffneterKreis[] = [];
  for (const k of kreise) {
    const offen = geoeffnet.get(k.id);
    const roh = roheSchluessel.get(k.id);
    if (!offen || !roh) continue;
    const bezeichnungsSchluessel = await deriveKreisBezeichnungsSchluessel(roh);
    const bezeichnung = utf8.decode(await decrypt(bezeichnungsSchluessel, fromBase64(k.bezeichnungEnc)));
    ergebnis.push({ ...offen, bezeichnung });
  }
  return ergebnis;
}

export interface NeuerKreis {
  art: 'ordner' | 'marker';
  elternId?: string | null;
  bezeichnung: string;
  /**
   * Zusätzliche Personen, für die der neue Kreis direkt gewrappt werden
   * soll (Public Key, base64). Ein Unterordner braucht das in der Regel
   * nicht — er erbt automatisch vom Elternordner (siehe unten).
   */
  weiterePersonen?: Array<{ personId: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm }>;
}

/**
 * Legt einen neuen Kreis an: erzeugt sein Schlüsselpaar lokal, verschlüsselt
 * die Bezeichnung damit, wrappt den privaten Schlüssel für die eigene Person
 * und — bei einem Unterordner — zusätzlich für den Elternordner (damit die
 * Vererbung nach unten automatisch greift), sowie für jede zusätzlich
 * angegebene Person.
 *
 * `eigenePersonId` kommt vom Aufrufer (`state.me.personId` in
 * `ansichten/gemeinsam.ts`) — der Server übernimmt jeden `zielId`-Wert
 * unverändert (`backend/src/routes/schluesselkreise.ts`), er füllt die
 * eigene Person nicht selbst ein.
 */
export async function kreisAnlegen(
  eingabe: NeuerKreis,
  eltern: GeoeffneterKreis | null,
  eigenePersonId: string,
): Promise<string> {
  const vault = requireVault();
  const organisation = requireOrganisation();

  const neu = await generateKeyPair(organisation.algorithm);
  const bezeichnungsSchluessel = await deriveKreisBezeichnungsSchluessel(neu.privateKeyPkcs8);
  const bezeichnungEnc = await encrypt(bezeichnungsSchluessel, utf8.encode(eingabe.bezeichnung));

  // Eigener Zugriff: für die eigene Person wrappen, damit der Anlegende den
  // Kreis sofort selbst sieht.
  const wraps: Array<{ zielArt: 'person' | 'kreis'; zielId: string; wrappedPrivateKey: string }> = [
    {
      zielArt: 'person',
      zielId: eigenePersonId,
      wrappedPrivateKey: toBase64(
        await wrapBytesFor(neu.privateKeyPkcs8, vault.publicKeyRaw, vault.algorithm),
      ),
    },
  ];

  if (eingabe.elternId && eltern) {
    wraps.push({
      zielArt: 'kreis',
      zielId: eltern.id,
      wrappedPrivateKey: toBase64(
        await wrapBytesFor(neu.privateKeyPkcs8, eltern.publicKeyRaw, eltern.algorithm),
      ),
    });
  }

  for (const person of eingabe.weiterePersonen ?? []) {
    wraps.push({
      zielArt: 'person',
      zielId: person.personId,
      wrappedPrivateKey: toBase64(
        await wrapBytesFor(neu.privateKeyPkcs8, person.publicKeyRaw, person.algorithm),
      ),
    });
  }

  const { id } = await api.post<{ id: string }>('/api/kreise', {
    art: eingabe.art,
    elternId: eingabe.elternId ?? null,
    algorithm: neu.algorithm,
    publicKey: toBase64(neu.publicKeyRaw),
    bezeichnungEnc: toBase64(bezeichnungEnc),
    wraps,
  });
  return id;
}

export async function kreisLoeschen(id: string): Promise<void> {
  await api.delete(`/api/kreise/${id}`);
}

/**
 * Verschiebt ein bereits abgelegtes Dokument in einen Kreis: wrappt seinen
 * DEK für den Kreis neu und lässt den Server jeden bisherigen Organisations-
 * /Kreis-Umschlag ersetzen (`backend/src/routes/documents.ts`,
 * `POST /api/documents/:id/verschieben`). Ab jetzt sehen nur noch Mitglieder
 * dieses Kreises das Dokument — nicht mehr automatisch die ganze
 * Organisation.
 */
export async function dokumentInKreisVerschieben(
  documentId: string,
  dek: CryptoKey,
  ziel: GeoeffneterKreis,
): Promise<void> {
  const wrappedDek = toBase64(await wrapDekFor(dek, ziel.publicKeyRaw, ziel.algorithm));
  await api.post(`/api/documents/${documentId}/verschieben`, {
    ziel: 'kreis',
    kreisId: ziel.id,
    wrappedDek,
  });
}

/**
 * Verschiebt ein Dokument (zurück) in die Sichtbarkeit der ganzen
 * Organisation — das Gegenstück zu `dokumentInKreisVerschieben`.
 */
export async function dokumentInOrganisationVerschieben(documentId: string, dek: CryptoKey, safe?: { publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm }): Promise<void> {
  const organisation = safe ?? requireOrganisation();
  const wrappedDek = toBase64(await wrapDekFor(dek, organisation.publicKeyRaw, organisation.algorithm));
  await api.post(`/api/documents/${documentId}/verschieben`, {
    ziel: 'organisation',
    wrappedDek,
  });
}

// ---------------------------------------------------------------------------
// Fächer: ein Ordner eines gemeinsamen Safes nur für bestimmte Leute (17.10.2026)
// ---------------------------------------------------------------------------

/** Das Fach, in dem ein Ordner liegt (der Ordner selbst oder ein Elternordner), falls eines offen ist. */
export function fachFuerOrdner(
  folderId: string | null,
  kreise: GeoeffneterKreis[],
  elternVon: (id: string) => string | null,
): GeoeffneterKreis | null {
  let aktuell = folderId;
  const gesehen = new Set<string>();
  while (aktuell && !gesehen.has(aktuell)) {
    gesehen.add(aktuell);
    const treffer = kreise.find((k) => k.folderId === aktuell);
    if (treffer) return treffer;
    aktuell = elternVon(aktuell);
  }
  return null;
}

/**
 * Macht einen Ordner zum Fach: neuer Kreis am Ordner, Umschläge für mich und die
 * gewählten Leute, alle Dokumente darin (samt Unterordnern) für den Kreis statt für
 * den ganzen Safe verpackt.
 */
export async function fachAnlegen(
  safe: { kundeId: string; algorithm: KeyAlgorithm },
  folder: { id: string; name: string },
  eigenePersonId: string,
  personen: Array<{ personId: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm }>,
  dokumente: Array<{ id: string; dek: CryptoKey }>,
): Promise<string> {
  const vault = requireVault();
  const neu = await generateKeyPair(safe.algorithm);
  const bezeichnungsSchluessel = await deriveKreisBezeichnungsSchluessel(neu.privateKeyPkcs8);
  const bezeichnungEnc = await encrypt(bezeichnungsSchluessel, utf8.encode(folder.name));
  const wraps = [
    { personId: eigenePersonId, wrappedPrivateKey: toBase64(await wrapBytesFor(neu.privateKeyPkcs8, vault.publicKeyRaw, vault.algorithm)) },
  ];
  for (const p of personen) {
    if (p.personId === eigenePersonId) continue;
    wraps.push({ personId: p.personId, wrappedPrivateKey: toBase64(await wrapBytesFor(neu.privateKeyPkcs8, p.publicKeyRaw, p.algorithm)) });
  }
  const dokumentWraps = [];
  for (const d of dokumente) {
    dokumentWraps.push({ documentId: d.id, wrappedDek: toBase64(await wrapDekFor(d.dek, neu.publicKeyRaw, neu.algorithm)) });
  }
  const { id } = await api.post<{ id: string }>(`/api/safes/${safe.kundeId}/faecher`, {
    folderId: folder.id,
    algorithm: neu.algorithm,
    publicKey: toBase64(neu.publicKeyRaw),
    bezeichnungEnc: toBase64(bezeichnungEnc),
    wraps,
    dokumente: dokumentWraps,
  });
  kreisCache = null;
  return id;
}

/** Leute eines Fachs ändern. */
export async function fachPersonenAendern(
  kundeId: string,
  kreis: GeoeffneterKreis,
  hinzu: Array<{ personId: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm }>,
  entfernen: string[],
): Promise<void> {
  const wraps = [];
  for (const p of hinzu) {
    wraps.push({ personId: p.personId, wrappedPrivateKey: toBase64(await wrapBytesFor(kreis.privateKeyPkcs8, p.publicKeyRaw, p.algorithm)) });
  }
  await api.put(`/api/safes/${kundeId}/faecher/${kreis.id}`, { wraps, entfernen });
  kreisCache = null;
}

/** Fach auflösen: Dokumente wieder für den ganzen Safe verpacken. */
export async function fachAufloesen(
  safe: { kundeId: string; publicKeyRaw: Uint8Array; algorithm: KeyAlgorithm },
  kreisId: string,
  dokumente: Array<{ id: string; dek: CryptoKey }>,
): Promise<void> {
  const dokumentWraps = [];
  for (const d of dokumente) {
    dokumentWraps.push({ documentId: d.id, wrappedDek: toBase64(await wrapDekFor(d.dek, safe.publicKeyRaw, safe.algorithm)) });
  }
  await api.delete(`/api/safes/${safe.kundeId}/faecher/${kreisId}`, { dokumente: dokumentWraps });
  kreisCache = null;
}

/**
 * Nimmt ein weiteres Mitglied der Organisation in einen bereits bestehenden
 * Kreis auf: wrappt den Kreisschlüssel für dessen öffentlichen Personen-
 * Schlüssel (`GET /api/keys/person/:id/public`, nur für aktive Mitglieder
 * derselben Organisation erreichbar) und reicht den Umschlag an den Server
 * weiter (`POST /api/kreise/:id/wraps`).
 *
 * Setzt voraus, dass der Kreis bereits über `ladeKreiseGecacht()` entsperrt
 * ist — man kann nur weitergeben, was man selbst öffnen kann.
 */
export async function kreisMitgliedHinzufuegen(
  kreisId: string,
  empfaengerPersonId: string,
  empfaengerPublicKeyRaw: Uint8Array,
  empfaengerAlgorithm: KeyAlgorithm,
): Promise<void> {
  const kreise = await ladeKreiseGecacht();
  const kreis = kreise.find((k) => k.id === kreisId);
  if (!kreis) throw new Error('Dieser Kreis ist nicht entsperrt — Hinzufügen nicht möglich');

  const wrappedPrivateKey = toBase64(
    await wrapBytesFor(kreis.privateKeyPkcs8, empfaengerPublicKeyRaw, empfaengerAlgorithm),
  );
  await api.post(`/api/kreise/${kreisId}/wraps`, {
    wraps: [{ zielArt: 'person', zielId: empfaengerPersonId, wrappedPrivateKey }],
  });
}
